import { useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { panelStatusAtom, labelAtom, displayTasksAtom, removeTaskAtom } from '../@atoms';
import { DisplayTask, ExpectedPrepareResponse } from '../@types';

export const useManagePanel = () => {
  const [panelStatus, setPanelStatus] = useAtom(panelStatusAtom);
  const panelLabel = useAtomValue(labelAtom);
  const displayTasks = useAtomValue(displayTasksAtom);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const handleToggleFold = () => {
    setPanelStatus((prev) => ({ ...prev, isFold: !prev.isFold }));
  };
  const handleClickClose = () => {
    const hasProcessingOrReadyTask = displayTasks.some(
      (task) => task.status == 'prepare' || task.status == 'processing'
    );
    if (hasProcessingOrReadyTask) {
      setShowCancelConfirm(true);
    } else {
      terminateDownload();
    }
  };

  const { remove } = useRemoveTask();
  const terminateDownload = async () => {
    for (const task of displayTasks) {
      await remove(task);
    }
    setPanelStatus((prev) => ({ ...prev, isOpen: false }));
    setShowCancelConfirm(false);
  };
  const continueDownload = () => {
    setShowCancelConfirm(false);
  };

  return {
    panel: {
      panelStatus,
      handleToggleFold,
      handleClickClose,
      panelLabel,
    },
    tasks: displayTasks,
    confirm: {
      isOpen: showCancelConfirm,
      setIsOpen: setShowCancelConfirm,
      continue: continueDownload,
      terminate: terminateDownload,
    },
  };
};

export const useRemoveTask = () => {
  const removeTask = useSetAtom(removeTaskAtom);
  const displayTasks = useAtomValue(displayTasksAtom);

  const remove = async (task: DisplayTask<ExpectedPrepareResponse, unknown, unknown, unknown>) => {
    if (task.status === 'completed' || task.status === 'failed') {
      removeTask({ taskId: task.taskId });
    } else {
      if (task) {
        await task.removeFn(task);
        removeTask({ taskId: task.taskId });
      }
    }
    task.onTaskRemoved?.(task, null);
  };
  const removeByTaskId = async (taskId: number) => {
    const task = displayTasks.find((t) => t.taskId === taskId);
    if (task) {
      await remove(task);
    }
  };

  return {
    remove,
    removeByTaskId,
  };
};
